import {
  DAY_MINUTES, ShortTermCalendarData, ShortTermConfig,
  durationLabel, estimatePrice, findFirstFreeStart, fitDuration, formatWall, getDefaultDuration, getDurationOptions,
  getHorizon, getSlotStep, isWindowFree, nowWall, parseBusy, parseWall, roundDown, roundUp,
  wallDay, wallDayLabel, wallTime
} from 'utils/shortTerm'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import useTranslation from 'hooks/useTranslation'
import { displayPrice } from 'utils/string'
import './style.scss'

export interface RentalWindowPickerProps {
  shortTerm: ShortTermConfig
  calendar?: ShortTermCalendarData
  startDateTime?: string
  durationMinutes?: number
  onChange: (startDateTime: string, durationMinutes: number) => void
}

/**
 * Wybór okna najmu krótkoterminowego: dzień odbioru, godzina, długość (progi cennika + stepper).
 * Wspólny dla OrderDetails (ShortTermCalendar) i modala wynajmu ze sklepu — dane z checkPrice (shortTerm + calendar).
 * Okno to czas ścienny placu ("RRRR-MM-DD GG:MM") + minuty.
 */
const RentalWindowPicker: React.FC<RentalWindowPickerProps> = ({ shortTerm, calendar, startDateTime, durationMinutes, onChange }) => {
  const { t, lang } = useTranslation('calendar.shortTerm')

  // "teraz" w strefie placu — odświeżane co minutę, żeby nie proponować godzin, które już minęły
  const [now, setNow] = useState(() => nowWall())
  useEffect(() => {
    const interval = setInterval(() => setNow(nowWall()), 60000)
    return () => clearInterval(interval)
  }, [])

  const busy = useMemo(() => parseBusy(calendar?.busy), [calendar?.busy])
  const horizon = useMemo(() => getHorizon(calendar, now), [calendar, now])

  const unit = Math.max(1, Number(shortTerm.unitMinutes) || 30)
  const step = getSlotStep(shortTerm)
  const hourStep = roundUp(60, unit)
  const minMinutes = Math.max(Number(shortTerm.minMinutes) || unit, unit)
  const maxMinutes = shortTerm.maxMinutes ? Number(shortTerm.maxMinutes) : Infinity
  const firstSlot = roundUp(horizon.from, step)

  const start = parseWall(startDateTime)
  const duration = durationMinutes || getDefaultDuration(shortTerm)

  const [selectedDay, setSelectedDay] = useState<number | undefined>(start !== undefined ? wallDay(start) : undefined)
  useEffect(() => {
    if (start !== undefined) setSelectedDay(wallDay(start))
  }, [start])

  const days = useMemo(() => {
    const list: { day: number, available: boolean }[] = []
    for (let day = wallDay(firstSlot); day < horizon.to; day += DAY_MINUTES) {
      const from = Math.max(day, firstSlot)
      const until = Math.min(day + DAY_MINUTES, horizon.to)
      list.push({ day, available: findFirstFreeStart(busy, from, until, minMinutes, step) !== undefined })
    }
    return list
  }, [busy, firstSlot, horizon.to, minMinutes, step])

  const slots = useMemo(() => {
    if (selectedDay === undefined) return []
    const list: { value: number, available: boolean }[] = []
    const until = Math.min(selectedDay + DAY_MINUTES, horizon.to)
    for (let s = roundUp(Math.max(selectedDay, firstSlot), step); s < until; s += step) {
      list.push({ value: s, available: isWindowFree(busy, s, s + minMinutes) })
    }
    return list
  }, [busy, firstSlot, horizon.to, minMinutes, selectedDay, step])

  const durationOptions = useMemo(() => getDurationOptions(shortTerm), [shortTerm])

  const canUse = (s: number | undefined, d: number) => s !== undefined && d >= minMinutes && d <= maxMinutes && isWindowFree(busy, s, s + d)
  const save = (s: number, d: number) => onChange(formatWall(s), d)

  const selectDay = (day: number) => {
    setSelectedDay(day)
    if (start !== undefined && wallDay(start) === day) return
    const from = Math.max(day, firstSlot)
    const until = Math.min(day + DAY_MINUTES, horizon.to)
    const s = findFirstFreeStart(busy, from, until, duration, step) ?? findFirstFreeStart(busy, from, until, minMinutes, step)
    if (s !== undefined) save(s, fitDuration(shortTerm, busy, s, duration))
  }

  // przewinięcie listy dni do wybranego dnia (np. domyślny termin za kilka dni)
  const daysRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const selected = daysRef.current?.querySelector('.--selected') as HTMLElement | null
    if (selected && daysRef.current) {
      daysRef.current.scrollLeft = selected.offsetLeft - daysRef.current.offsetLeft - 16
    }
  }, [selectedDay])

  const dayLabel = (day: number) => {
    const today = wallDay(now)
    if (day === today) return t('today')
    if (day === today + DAY_MINUTES) return t('tomorrow')
    return wallDayLabel(day, lang, { weekday: 'short' })
  }

  const shorter = roundUp(duration, hourStep) - hourStep
  const longer = roundDown(duration, hourStep) + hourStep

  if (days.length > 0 && days.every(d => !d.available)) {
    return <div className='shortTermCalendar__message --warning'>{t('noAvailability')}</div>
  }

  return (
    <div className='shortTermCalendar__body'>
      <div className='shortTermCalendar__label'>{t('pickupDay')}</div>
      <div
        ref={daysRef}
        className='shortTermCalendar__days'>
        {days.map(({ day, available }) => <button
          key={day}
          type='button'
          disabled={!available}
          className={`shortTermCalendar__day${day === selectedDay ? ' --selected' : ''}`}
          onClick={() => selectDay(day)}>
          <span className='shortTermCalendar__day__name'>{dayLabel(day)}</span>
          <span className='shortTermCalendar__day__date --secFont'>{wallDayLabel(day, lang, { day: '2-digit', month: '2-digit' })}</span>
        </button>)}
      </div>

      <div className='shortTermCalendar__label'>{t('pickupTime')}</div>
      {selectedDay === undefined
        ? <div className='shortTermCalendar__message'>{t('selectPickup')}</div>
        : slots.some(s => s.available)
          ? <div className='shortTermCalendar__chips'>
            {slots.map(({ value, available }) => <button
              key={value}
              type='button'
              disabled={!available}
              className={`shortTermCalendar__chip --secFont${value === start ? ' --selected' : ''}`}
              onClick={() => save(value, fitDuration(shortTerm, busy, value, duration))}>
              {wallTime(value)}
            </button>)}
          </div>
          : <div className='shortTermCalendar__message'>{t('noSlotsThisDay')}</div>}
      <div className='shortTermCalendar__hint'>{t('busyInfo')}</div>

      <div className='shortTermCalendar__label'>{t('duration')}</div>
      <div className='shortTermCalendar__chips --durations'>
        {durationOptions.map(option => {
          const price = estimatePrice(shortTerm, option)
          return <button
            key={option}
            type='button'
            disabled={!canUse(start, option)}
            className={`shortTermCalendar__chip --duration${option === duration ? ' --selected' : ''}`}
            onClick={() => { if (start !== undefined) save(start, option) }}>
            <span className='--secFont'>{durationLabel(option, lang)}</span>
            {price && <span className='shortTermCalendar__chip__price'>{displayPrice(price.gross)}</span>}
          </button>
        })}
      </div>

      <div className='shortTermCalendar__stepper'>
        <span>{t('otherDuration')}</span>
        <div className='shortTermCalendar__stepper__controls'>
          <button
            type='button'
            aria-label={t('decrease')}
            disabled={start === undefined || duration <= minMinutes}
            onClick={() => { if (start !== undefined) save(start, Math.max(minMinutes, shorter)) }}>
            -
          </button>
          <span className='--secFont'>{durationLabel(duration, lang)}</span>
          <button
            type='button'
            aria-label={t('increase')}
            disabled={!canUse(start, longer)}
            onClick={() => { if (start !== undefined) save(start, longer) }}>
            +
          </button>
        </div>
      </div>
    </div>
  )
}

export default RentalWindowPicker
